/**
 * WHITE MOCCA MOTION — runtime core
 *
 * The single JavaScript entry point for every White Mocca animation. Nothing in
 * `sections/`, `snippets/` or `blocks/` should ever contain animation script: markup opts
 * into an effect declaratively (`data-wm-reveal`, `data-wm-stagger`, `data-wm-parallax`,
 * `data-wm-split`) and this runtime wires it up.
 *
 * Responsibilities, and nothing else:
 *
 *   1. Read the capability state (motion level, reduced motion, device power) and publish it
 *      on `<html>` so the stylesheet can react to it.
 *   2. Hold a registry of effects, keyed by the attribute that activates them.
 *   3. Scan the document for those attributes and mount/unmount effect instances.
 *   4. Re-scan after the Section Rendering API, morph, and Theme Editor events, so effects
 *      survive filters, infinite scroll, variant changes and editor edits.
 *   5. Absorb Ritual's scroll-container quirk once, in `observeInViewport`, so no effect ever
 *      has to remember it.
 *
 * This file registers ZERO effects. Later phases add them via `registerEffect`.
 *
 * ---------------------------------------------------------------------------------------
 * THE VISIBILITY CONTRACT — the one rule every effect must obey
 * ---------------------------------------------------------------------------------------
 * An element must never end up permanently invisible. Concretely:
 *
 *   - Content is visible by default. An effect may only *hide* an element after it has
 *     confirmed it can also reveal it.
 *   - The CSS-only path (`@supports (animation-timeline: view())`) may hide at first paint,
 *     because the browser guarantees the reveal without JavaScript. It is also immune to
 *     morph, since the animation lives in the cascade rather than in element state.
 *   - The JavaScript path may only hide elements that are *outside* the viewport at mount
 *     time. Anything already on screen is marked revealed immediately, with no animation.
 *     That removes any flash of hidden content, and means a JS failure costs an animation,
 *     never the content.
 *   - `morph` does not preserve arbitrary attributes (see `assets/morph.js`
 *     PRESERVED_ATTRIBUTES), so a revealed marker written here is stripped on re-render. The
 *     failure mode is therefore deliberately "visible again", not "hidden forever".
 * ---------------------------------------------------------------------------------------
 */

import { isLowPowerDevice, prefersReducedMotion, requestIdleCallback } from '@theme/utilities';
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

const ROOT = document.documentElement;

/** @typedef {'off' | 'subtle' | 'full'} MotionLevel */

/**
 * The handle an effect returns from `mount`, letting the runtime manage its lifecycle.
 *
 * @typedef {object} EffectHandle
 * @property {() => void} [destroy] - Release observers, listeners and inline styles.
 * @property {() => void} [refresh] - Re-apply state the element lost. Called on every scan
 *   for already-mounted elements, which is how effects recover from a morph that stripped
 *   their state attributes.
 */

/**
 * @typedef {object} EffectContext
 * @property {Element} element - The element carrying the activating attribute.
 * @property {string} value - The raw attribute value, e.g. `"fade-up"` or `"0.15"`.
 * @property {MotionRuntime} runtime - The runtime, for reading capability state.
 */

/**
 * @typedef {object} EffectDefinition
 * @property {string} attribute - The attribute that activates this effect, e.g. `data-wm-reveal`.
 * @property {MotionLevel[]} [levels] - Motion levels this effect runs at. Defaults to both.
 * @property {boolean} [motionSensitive] - Unmounted when the user prefers reduced motion.
 *   Defaults to `true`; set `false` only for effects that convey no movement.
 * @property {boolean} [expensive] - Unmounted on low-power devices. Use for scroll-linked or
 *   DOM-rewriting effects such as parallax and split text. Defaults to `false`.
 * @property {(context: EffectContext) => EffectHandle | void} mount - Activates the effect.
 */

/**
 * Whether the browser can run CSS scroll-driven animations.
 *
 * When true, reveal effects are expected to take the CSS-only path: no observer, no element
 * state, and no re-wiring after a morph. Ritual already relies on this API in
 * `snippets/slideshow-styles.liquid` and `blocks/_slide.liquid`, always behind `@supports`.
 *
 * @type {boolean}
 */
export const supportsViewTimeline =
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('animation-timeline: view()');

/**
 * Observes an element entering the scrollport, using Ritual's scroll container.
 *
 * This exists so no effect has to remember the quirk: above 990px `html` and `body` are
 * `overflow: hidden` and the real scroller is `.page-wrapper`, so an IntersectionObserver
 * built without an explicit root works on mobile and silently never fires on desktop.
 * `getIntersectionRoot()` returns the right root for the current breakpoint, and the
 * observer is rebuilt when the viewport crosses that breakpoint — the same approach
 * `assets/header.js` takes.
 *
 * @param {Element} element - The element to watch.
 * @param {(entry: IntersectionObserverEntry) => void} onEnter - Called when it enters view.
 * @param {object} [options]
 * @param {number | number[]} [options.threshold] - Intersection threshold(s).
 * @param {string} [options.rootMargin] - Margin around the root.
 * @param {boolean} [options.once] - Stop observing after the first entry. Defaults to `true`.
 * @returns {() => void} A teardown function.
 */
export function observeInViewport(element, onEnter, options = {}) {
  const { threshold = 0.15, rootMargin = '0px 0px -8% 0px', once = true } = options;

  /** @type {IntersectionObserver | null} */
  let observer = null;

  const build = () => {
    observer?.disconnect();
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          onEnter(entry);
          if (once) observer?.unobserve(entry.target);
        }
      },
      { root: getIntersectionRoot(), rootMargin, threshold }
    );
    observer.observe(element);
  };

  build();

  // The scroll container swaps between `.page-wrapper` and the document at 990px, and an
  // IntersectionObserver's root is fixed at construction, so it has to be rebuilt.
  const onBreakpointChange = () => {
    if (element.isConnected) build();
  };
  scrollContainerMediaQuery.addEventListener('change', onBreakpointChange);

  return () => {
    scrollContainerMediaQuery.removeEventListener('change', onBreakpointChange);
    observer?.disconnect();
    observer = null;
  };
}

/**
 * Returns whether an element is currently within the scrollport.
 *
 * Used to honour the visibility contract: an effect must not hide something the shopper can
 * already see, because the runtime starts after first paint.
 *
 * @param {Element} element
 * @returns {boolean}
 */
export function isInViewport(element) {
  const root = getIntersectionRoot();
  const bounds = root ? root.getBoundingClientRect() : null;
  const top = bounds ? bounds.top : 0;
  const bottom = bounds ? bounds.bottom : window.innerHeight || ROOT.clientHeight;
  const rect = element.getBoundingClientRect();

  return rect.top < bottom && rect.bottom > top;
}

class MotionRuntime {
  /** @type {Map<string, EffectDefinition>} */
  #effects = new Map();

  /** @type {Map<Element, Map<string, EffectHandle>>} */
  #mounted = new Map();

  #reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  #lowPower = isLowPowerDevice();

  #scanScheduled = false;

  #started = false;

  /**
   * The configured motion level, read from the `data-wm-motion` attribute that
   * `snippets/wm-motion.liquid` writes onto `<html>` from the theme setting.
   *
   * @returns {MotionLevel}
   */
  get level() {
    const value = ROOT.getAttribute('data-wm-motion');
    return value === 'subtle' || value === 'full' ? value : 'off';
  }

  /** @returns {boolean} */
  get reducedMotion() {
    return prefersReducedMotion();
  }

  /** @returns {boolean} */
  get lowPower() {
    return this.#lowPower;
  }

  /** @returns {boolean} Whether any White Mocca effect may run right now. */
  get active() {
    return this.level !== 'off' && !this.reducedMotion;
  }

  /**
   * Registers an effect. Safe to call before or after the runtime starts.
   *
   * @param {string} name - Unique effect name, used for diagnostics.
   * @param {EffectDefinition} definition
   */
  registerEffect(name, definition) {
    if (this.#effects.has(name)) {
      // Re-registration would orphan the mounted instances of the previous definition.
      return;
    }
    this.#effects.set(name, definition);
    if (this.#started) this.scheduleScan();
  }

  /**
   * Whether an effect is allowed to run under the current capability state.
   *
   * @param {EffectDefinition} definition
   * @returns {boolean}
   */
  #isAllowed(definition) {
    if (!this.active) return false;

    const levels = definition.levels ?? ['subtle', 'full'];
    if (!levels.includes(this.level)) return false;

    if (definition.expensive && this.#lowPower) return false;

    return true;
  }

  /**
   * Mounts, refreshes or leaves alone a single element/effect pair.
   *
   * @param {Element} element
   * @param {string} name
   * @param {EffectDefinition} definition
   */
  #mount(element, name, definition) {
    let handles = this.#mounted.get(element);

    if (handles?.has(name)) {
      // Already mounted. `refresh` is the effect's chance to restore state that a morph
      // re-render stripped off the element.
      handles.get(name)?.refresh?.();
      return;
    }

    const value = element.getAttribute(definition.attribute) ?? '';
    const handle = definition.mount({ element, value, runtime: this }) ?? {};

    if (!handles) {
      handles = new Map();
      this.#mounted.set(element, handles);
    }
    handles.set(name, handle);
  }

  /**
   * Unmounts one effect, or every effect, from an element.
   *
   * @param {Element} element
   * @param {string} [name] - Omit to unmount all of the element's effects.
   */
  #unmount(element, name) {
    const handles = this.#mounted.get(element);
    if (!handles) return;

    for (const [key, handle] of handles) {
      if (name && key !== name) continue;
      handle.destroy?.();
      handles.delete(key);
    }

    if (handles.size === 0) this.#mounted.delete(element);
  }

  /**
   * Drops effects whose element left the document, and effects the current capability state
   * no longer allows.
   *
   * Elements are pruned by checking `isConnected` rather than by watching removals, which
   * keeps this correct however the node left — morph, drawer teardown or editor reload —
   * and stops the runtime holding removed nodes alive.
   */
  #prune() {
    for (const [element, handles] of this.#mounted) {
      if (!element.isConnected) {
        this.#unmount(element);
        continue;
      }

      for (const name of handles.keys()) {
        const definition = this.#effects.get(name);
        if (!definition || !this.#isAllowed(definition)) this.#unmount(element, name);
      }
    }
  }

  /**
   * Finds every element opting into a registered effect and mounts it.
   *
   * Cheap enough to run on every DOM change once debounced to idle: it is one
   * `querySelectorAll` per registered effect, and already-mounted elements short-circuit.
   */
  scan() {
    this.#scanScheduled = false;
    this.#prune();

    if (this.level === 'off') return;

    for (const [name, definition] of this.#effects) {
      if (!this.#isAllowed(definition)) continue;

      for (const element of document.querySelectorAll(`[${definition.attribute}]`)) {
        this.#mount(element, name, definition);
      }
    }
  }

  /**
   * Queues a scan for the next idle period, collapsing bursts of DOM mutations into one pass.
   */
  scheduleScan() {
    if (this.#scanScheduled) return;
    this.#scanScheduled = true;
    requestIdleCallback(() => this.scan());
  }

  /**
   * Re-evaluates capability state and reconciles every mounted effect against it.
   *
   * Reduced motion can be toggled mid-session from the OS, so this has to tear effects down
   * as well as build them up.
   */
  #sync() {
    ROOT.toggleAttribute('data-wm-reduced', this.reducedMotion);
    this.#prune();
    this.scheduleScan();
  }

  /**
   * Watches every source of DOM change the theme has.
   *
   * Neither `morph` nor `section-renderer` emits an event, so a MutationObserver is the only
   * general signal for Section Rendering API updates — filters, infinite scroll, variant
   * changes, cart drawer contents. The Theme Editor events are added on top because they
   * also fire for section reorders that may not otherwise touch `<body>`'s subtree.
   */
  #observe() {
    new MutationObserver(() => this.scheduleScan()).observe(document.body, {
      childList: true,
      subtree: true,
    });

    for (const event of ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder']) {
      document.addEventListener(event, () => this.scheduleScan());
    }

    this.#reduceQuery.addEventListener('change', () => this.#sync());
  }

  /**
   * Boots the runtime. Idempotent.
   */
  start() {
    if (this.#started) return;
    this.#started = true;

    if (this.level === 'off') return;

    // Published for the stylesheet. Effects that cost main-thread work opt out of low-power
    // devices via `expensive: true`; the attribute lets CSS make the same call.
    if (this.#lowPower) ROOT.setAttribute('data-wm-power', 'low');
    ROOT.toggleAttribute('data-wm-reduced', this.reducedMotion);

    // Signals that the runtime is alive. The stylesheet uses it to enable anything that
    // would strand content if the script never arrived.
    ROOT.setAttribute('data-wm-motion-ready', '');

    this.#observe();
    this.scan();
  }

  /**
   * Diagnostics for the console. Not used by any effect.
   *
   * @returns {{level: MotionLevel, active: boolean, reducedMotion: boolean, lowPower: boolean,
   *   supportsViewTimeline: boolean, effects: string[], mounted: number}}
   */
  inspect() {
    return {
      level: this.level,
      active: this.active,
      reducedMotion: this.reducedMotion,
      lowPower: this.#lowPower,
      supportsViewTimeline,
      effects: [...this.#effects.keys()],
      mounted: this.#mounted.size,
    };
  }
}

/** The one runtime instance. */
export const motion = new MotionRuntime();

/**
 * Registers an effect with the runtime.
 *
 * @param {string} name
 * @param {EffectDefinition} definition
 */
export function registerEffect(name, definition) {
  motion.registerEffect(name, definition);
}

// `<body>` must exist before the MutationObserver can attach. The script is a deferred
// module, so it normally does; the guard covers the editor injecting the module early.
if (document.body) {
  motion.start();
} else {
  document.addEventListener('DOMContentLoaded', () => motion.start(), { once: true });
}

// Exposed for debugging from the console and for later phases loaded as separate modules.
// Read `window.WMMotion.inspect()` to see the live capability state. Cast locally rather than
// declaring the property in `assets/global.d.ts`, to keep this layer out of Ritual's files.
/** @type {Window & { WMMotion?: MotionRuntime }} */ (window).WMMotion = motion;
